import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Loader2 } from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeName } from "@/components/EmployeeName";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const fmtDay = (iso) => (iso ? iso.slice(8, 10) : "");
const toMin = (hhmm) => { if (!hhmm) return null; const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
const fromMin = (m) => (m == null ? "" : `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);

const COMP_COLORS = { full: "#0E8A46", half: "#0058C7", zero: "#C8102E", needs: "#B5590F" };

const StatCard = ({ testid, label, value, tone = "gray", onClick }) => {
  const tones = {
    green: "bg-[#E9F9EF] text-[#0E8A46]", blue: "bg-[#EEF3FA] text-[#0058C7]",
    red: "bg-[#FDECEC] text-[#C0362C]", amber: "bg-[#FFF1E6] text-[#B5590F]",
    gray: "bg-[#F2F2F7] text-[#1D1D1F]", purple: "bg-[#F3EDFB] text-[#7A3EC2]",
  };
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick}
      className={`rounded-2xl px-4 py-3 text-left transition-all ${tones[tone]} ${onClick ? "hover:scale-[1.02] active:scale-[0.98] cursor-pointer" : "cursor-default"}`}>
      <p className="text-[24px] font-semibold leading-tight">{value}</p>
      <p className="text-[12px] font-medium opacity-80">{label}</p>
    </button>
  );
};

const ChartCard = ({ title, children }) => (
  <div className="liquid-glass-card p-5">
    <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-[#A1A1A6]">{title}</h3>
    {children}
  </div>
);

export default function MonthlyAnalytics() {
  const { t, months } = useT();
  const now = new Date();
  const [bulan, setBulan] = useState(10);
  const [tahun, setTahun] = useState(2026);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { title, rows }
  void now;

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/attendance/fingerprint/monthly-analytics?bulan=${bulan}&tahun=${tahun}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [bulan, tahun]);
  useEffect(() => { load(); }, [load]);

  const comp = useMemo(() => {
    if (!data) return [];
    return [
      { key: "full", name: t("ma.fullDay"), value: data.full_day },
      { key: "half", name: t("ma.halfDay"), value: data.half_day },
      { key: "zero", name: t("ma.zeroDay"), value: data.zero_day },
      { key: "needs", name: t("ma.needsReview"), value: data.needs_review },
    ].filter((x) => x.value > 0);
  }, [data, t]);

  const trendBar = useMemo(() => (data?.trend || []).map((t) => ({ day: fmtDay(t.date), Present: t.present, Full: t.full, Half: t.half, Needs: t.needs })), [data]);
  const trendTime = useMemo(() => (data?.trend || []).map((t) => ({ day: fmtDay(t.date), in: toMin(t.avg_in), out: toMin(t.avg_out) })), [data]);

  const openDetail = (title, rows) => setDetail({ title, rows: rows || [] });

  return (
    <div data-testid="monthly-analytics-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-[22px] font-semibold tracking-tight sm:text-[28px]">
            <BarChart3 className="h-6 w-6 text-[#0071E3]" /> {t("ma.title")}
          </h1>
          <p className="mt-0.5 text-[13px] text-[#8E8E93]">{t("ma.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(bulan)} onValueChange={(v) => setBulan(Number(v))}>
            <SelectTrigger data-testid="ma-month" className="h-10 w-[140px] rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-xl">{months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(tahun)} onValueChange={(v) => setTahun(Number(v))}>
            <SelectTrigger data-testid="ma-year" className="h-10 w-[100px] rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-xl">{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading && !data ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>
      ) : data?.before_golive ? (
        <div data-testid="ma-before-golive" className="liquid-glass-card grid place-items-center py-16 text-center text-[13.5px] text-[#8E8E93]">
          {t("ma.beforeGolive", { date: data.go_live?.slice(0, 10) })}
        </div>
      ) : data?.tracked_employees === 0 ? (
        <div data-testid="ma-empty" className="liquid-glass-card grid place-items-center py-16 text-center text-[13.5px] text-[#8E8E93]">
          {t("ma.noRecords", { label: data.label })}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-8">
            <StatCard testid="ma-tracked" label={t("ma.tracked")} value={data.tracked_employees} tone="gray" />
            <StatCard testid="ma-full" label={t("ma.fullDay")} value={data.full_day} tone="green" onClick={() => openDetail(t("ma.fullDay"), data.details.full)} />
            <StatCard testid="ma-half" label={t("ma.halfDay")} value={data.half_day} tone="blue" onClick={() => openDetail(t("ma.halfDay"), data.details.half)} />
            <StatCard testid="ma-zero" label={t("ma.zeroDay")} value={data.zero_day} tone="red" onClick={() => openDetail(t("ma.zeroDay"), data.details.zero)} />
            <StatCard testid="ma-needs" label={t("ma.needsReview")} value={data.needs_review} tone="amber" onClick={() => openDetail(t("ma.needsReview"), data.details.needs)} />
            <StatCard testid="ma-not-arrived" label={t("ma.notArrived")} value={data.not_arrived} tone="gray" onClick={() => openDetail(t("ma.notArrived"), data.details.not_arrived)} />
            <StatCard testid="ma-manual" label={t("ma.manualOverrides")} value={data.manual_overrides} tone="purple" onClick={() => openDetail(t("ma.manualOverrides"), data.details.manual)} />
            <StatCard testid="ma-missing" label={t("ma.missingClockOut")} value={data.missing_clock_out} tone="amber" onClick={() => openDetail(t("ma.missingClockOut"), data.details.missing)} />
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:w-1/2">
            <StatCard testid="ma-avg-in" label={t("ma.avgIn")} value={data.avg_clock_in || "—"} tone="gray" />
            <StatCard testid="ma-avg-out" label={t("ma.avgOut")} value={data.avg_clock_out || "—"} tone="gray" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title={t("ma.composition")}>
              <div data-testid="ma-chart-composition" style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={comp} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                      {comp.map((c) => <Cell key={c.key} fill={COMP_COLORS[c.key]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
            <ChartCard title={t("ma.dailyTrend")}>
              <div data-testid="ma-chart-trend" style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={trendBar} margin={{ left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Full" name={t("ma.fullDay")} stackId="a" fill="#0E8A46" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Half" name={t("ma.halfDay")} stackId="a" fill="#0058C7" />
                    <Bar dataKey="Needs" name={t("ma.needsReview")} stackId="a" fill="#B5590F" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
            <ChartCard title={t("ma.timeTrend")}>
              <div data-testid="ma-chart-time" style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={trendTime} margin={{ left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis domain={[360, 1080]} tickFormatter={fromMin} tick={{ fontSize: 11 }} width={46} />
                    <Tooltip formatter={(v) => fromMin(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="in" name={t("ma.avgIn")} stroke="#0E8A46" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="out" name={t("ma.avgOut")} stroke="#0058C7" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="ma-detail-dialog" className="max-h-[85vh] gap-0 overflow-y-auto rounded-[20px] border border-white/50 bg-white/90 p-6 backdrop-blur-2xl sm:max-w-[500px]">
          <DialogHeader className="text-left"><DialogTitle className="text-[18px] font-semibold">{detail?.title} · {data?.label}</DialogTitle></DialogHeader>
          <div className="mt-3 space-y-2">
            {(detail?.rows || []).length === 0 ? (
              <p className="py-8 text-center text-[13.5px] text-[#8E8E93]">{t("ma.noRecordsShort")}</p>
            ) : (
              detail.rows.map((r, i) => (
                <div key={i} data-testid={`ma-detail-row-${i}`} className="flex items-center gap-3 rounded-xl bg-[#FAFAFA] px-3 py-2.5">
                  <EmployeeAvatar id={r.employee_id} nama={r.employee_name} hasPhoto={r.has_photo} size={34} />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-[#1D1D1F]"><EmployeeName id={r.employee_id} name={r.employee_name} /><JaritBadge show={r.is_jarit} /></p>
                    <p className="num text-[11.5px] text-[#8E8E93]">
                      {r.employee_nip || "—"}{r.date ? ` · ${r.date}` : ""}{r.first ? ` · ${r.first}→${r.last || "—"}` : ""}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[12.5px] font-medium">{(r.result || "").replace(/_/g, " ")}</p>
                    {r.is_manual && r.reviewed_by && (
                      <p className="flex items-center justify-end gap-1 text-[10.5px] text-[#8E8E93]">{t("ma.by", { name: r.reviewed_by })} <VerifiedBadge size={11} /></p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
